import type { Dtos } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { sfaaClassExperienceImportsQueryKey } from "../sfaa-class-experience.keys";

/** Keeps upload state observable across tab unmounts, preventing a duplicate upload window. */
export const SFAA_CLASS_EXPERIENCE_IMPORT_MUTATION_KEY = ["importSfaaClassExperience"] as const;

/** A 202 means the long-running import was accepted; history polling supplies its final outcome. */
/** Dataset caches remain valid until the history watcher observes a terminal status. */
export const useImportSfaaClassExperience = () => {
	const { post } = useRequest();
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: SFAA_CLASS_EXPERIENCE_IMPORT_MUTATION_KEY,
		mutationFn: async (file: File) => {
			const formData = new FormData();
			formData.append("file", file);
			return await post<Dtos.Admin.SfaaClassExperience.Import.Accepted>(
				"/v2/admin/surety/sfaa-class-experience/imports",
				formData,
			);
		},
		/** Handles expected upload refusals inline instead of opening the global error modal. */
		onError: (error) => logError(error),
		onSettled: async () => {
			await queryClient.invalidateQueries({ queryKey: sfaaClassExperienceImportsQueryKey() });
		},
	});
};
